// src/App.js
import React, { useState, Suspense, lazy } from 'react';
import { CheckCircle } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminProvider, useAdmin } from './contexts/AdminContext';
import Navigation from './components/Navigation';
import Dashboard from './components/Dashboard';
import AddPlayerModal from './components/modals/AddPlayerModal';
import DeleteConfirmationModal from './components/modals/DeleteConfirmationModal';
import { useRatingSystem } from './hooks/useRatingSystem';
import OfflineIndicator from './components/OfflineIndicator';
import AppUpdateNotice from './components/AppUpdateNotice';
import useClubNavigation from './hooks/useClubNavigation';
import ClubHome from './components/ClubHome';
import ClubMore from './components/ClubMore';
import ClubAbout from './components/ClubAbout';
import AccessGate from './components/AccessGate';
import ErrorBoundary from './components/ErrorBoundary';
import AdminWorkspace, { AdminOverview, isAdminPage } from './components/AdminWorkspace';

// Route-level lazy components — initial bundle에서 제외
const AdminAccounts = lazy(() => import('./components/AdminAccounts'));
const SemesterArchive = lazy(() => import('./components/SemesterArchive'));
const ClubRequests = lazy(() => import('./components/ClubRequests'));
const VirtualMatchList = lazy(() => import('./components/VirtualMatchList'));
const MatchRegistration = lazy(() => import('./components/MatchRegistration'));
const PlayerDetail = lazy(() => import('./components/PlayerDetail'));
const PlayerSearch = lazy(() => import('./components/PlayerSearch'));
const EloExplanation = lazy(() => import('./components/EloExplanation'));
const ScheduleCalendar = lazy(() => import('./components/ScheduleCalendar'));
const Announcements = lazy(() => import('./components/Announcements'));
const AnnouncementsAdmin = lazy(() => import('./components/AnnouncementsAdmin'));
const TournamentNews = lazy(() => import('./components/TournamentNews'));
const PlayerRegistration = lazy(() => import('./components/PlayerRegistration'));
const TournamentRegister = lazy(() => import('./components/TournamentRegister'));
const ScheduleRegister = lazy(() => import('./components/ScheduleRegister'));

const RouteLoadingFallback = () => (
  <div className="flex justify-center items-center py-12">
    <div className="animate-spin rounded-full h-8 w-8 border-2 border-secondary border-t-primary"></div>
  </div>
);

// Vercel Analytics and Speed Insights (optional)
let SpeedInsights = () => null;
let Analytics = () => null;
try {
  SpeedInsights = require("@vercel/speed-insights/react").SpeedInsights;
} catch (e) {}
try {
  Analytics = require("@vercel/analytics/react").Analytics;
} catch (e) {}

function TennisRatingSystemContent() {
  const { route, navigate, setDashboardTab, listState } = useClubNavigation();
  const { view: currentView, playerId: selectedPlayerId, dashboardTab } = route;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const { loading: authLoading, user: currentUser } = useAuth();
  const { isAdmin, isChecking: adminChecking } = useAdmin();
  const handleSetCurrentView = navigate;
  const handlePlayerNavigation = (id) => navigate('playerDetail', id);
  const handleDashboardTabChange = setDashboardTab;

  const {
    players,
    matches,
    showSuccessMessage,
    setShowSuccessMessage,
    handleAddPlayer,
    handleMatchSubmit,
    handleBatchMatchSubmit,
    handleMatchDelete,
    handleMatchEdit,
    handleBulkMatchDelete,
    rankings,
    chartData,
    isLoading,
    loadError,
    fetchData,
  } = useRatingSystem();


  const renderView = () => {
    if (isAdminPage(currentView) && !isAdmin) return adminChecking ? <RouteLoadingFallback /> : <div className="club-panel club-page-narrow"><h1 className="club-title">운영진 로그인이 필요합니다</h1><p className="text-muted-foreground mb-5">인증 후 경기와 클럽 소식을 관리할 수 있습니다.</p><button className="club-button" onClick={() => navigate('more')}>운영진 로그인으로 이동</button></div>;
    const dataViews = ['dashboard', 'playerSearch', 'playerDetail', 'addMatch', 'playerRegistration'];
    if (dataViews.includes(currentView) && isLoading) return <RouteLoadingFallback />;
    if (dataViews.includes(currentView) && loadError) return <div className="club-error" role="alert"><p>선수와 경기 기록을 불러오지 못했습니다.</p><button className="club-text-link" onClick={() => fetchData()}>다시 시도</button></div>;
    switch (currentView) {
      case 'requests':
        return <ClubRequests key="guest-requests" players={players} playersLoading={isLoading} playersError={loadError} retryPlayers={fetchData} />;
      case 'accounts-admin': return <AdminAccounts />;
      case 'admin': return <AdminOverview navigate={navigate} players={players} matches={matches} dataLoading={isLoading} dataError={loadError} />;
      case 'equipment-admin':
      case 'requests-admin':
        return <ClubRequests key={`${currentView}-${currentUser?.id || 'session'}`} admin equipmentOnly={currentView === 'equipment-admin'} initialStatus={route.requestStatus} onStatusChange={requestStatus => navigate('requests-admin', null, { requestStatus })} players={players} playersLoading={isLoading} playersError={loadError} retryPlayers={fetchData} onApproveMatch={match => handleMatchSubmit(match, () => {}, () => {})} />;
      case 'home':
        return <ClubHome rankings={rankings} onRankings={tab => { setDashboardTab(tab); navigate('dashboard'); }} players={players} matches={matches} navigate={navigate} onPlayer={handlePlayerNavigation} isLoading={isLoading} loadError={loadError} retry={fetchData} />;
      case 'archive': return <SemesterArchive />;
      case 'more': return <ClubMore navigate={navigate} />;
      case 'about': return <ClubAbout onEnter={() => navigate('home')} />;
      case 'dashboard':
        return (
          <Dashboard
            players={players}
            matches={matches}
            rankings={rankings}
            chartData={chartData}
            setSelectedPlayerId={handlePlayerNavigation}
            setCurrentView={handleSetCurrentView}
            setShowAddPlayer={setShowAddPlayer}
            listState={listState}
            isLoading={isLoading}
            loadError={loadError}
            retry={fetchData}
            selectedDivision={dashboardTab}
            setSelectedDivision={handleDashboardTabChange}
          />
        );
      case 'playerSearch':
        return (
          <PlayerSearch
            players={players}
            rankings={rankings}
            listState={listState}
            setSelectedPlayerId={handlePlayerNavigation}
            setCurrentView={handleSetCurrentView}
          />
        );
      case 'playerDetail':
        return (
          <PlayerDetail
            players={players}
            matches={matches}
            rankings={rankings}
            selectedPlayerId={selectedPlayerId}
            setCurrentView={handleSetCurrentView}
            setSelectedPlayerId={handlePlayerNavigation}
          />
        );
      case 'matches':
        // 가상 스크롤이 적용된 새로운 MatchList 사용
        return (
          <VirtualMatchList
            matches={matches}
            players={players}
            setShowDeleteConfirm={setShowDeleteConfirm}
            handleBulkMatchDelete={handleBulkMatchDelete}
            handleMatchEdit={handleMatchEdit}
          />
        );
      case 'addMatch':
        return (
          <MatchRegistration
            players={players}
            matches={matches}
            handleMatchSubmit={handleMatchSubmit}
            handleBatchMatchSubmit={handleBatchMatchSubmit}
            setCurrentView={handleSetCurrentView}
            showSuccessMessage={showSuccessMessage}
            setShowSuccessMessage={setShowSuccessMessage}
          />
        );
      case 'playerRegistration':
        return (
          <PlayerRegistration
            players={players}
            matches={matches}
            onUpdate={fetchData}
          />
        );
      case 'tournamentNews':
        return (
          <TournamentNews
            players={players}
            setSelectedPlayerId={handlePlayerNavigation}
            setCurrentView={handleSetCurrentView}
          />
        );
      case 'tournamentRegister':
        return (
          <TournamentRegister
            players={players}
          />
        );
      case 'schedule':
        return <ScheduleCalendar navigate={navigate} />;
      case 'scheduleRegister':
        return <ScheduleRegister />;
      case 'announcements':
        // 공지사항 뷰
        return <Announcements />;
      case 'announcements-admin':
      case 'admin-login':
        // 공지사항 관리자 뷰 (또는 직접 URL 접근)
        return <AnnouncementsAdmin />;
      case 'eloExplanation':
        return <EloExplanation />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background font-sans antialiased overflow-x-hidden">
      <Navigation 
        setCurrentView={handleSetCurrentView} 
        setShowAddPlayer={setShowAddPlayer} 
        currentView={currentView} 
      />
      {showSuccessMessage && (
        <div role="status" aria-live="polite" className="fixed top-20 right-4 bg-primary text-white px-4 py-2 rounded-md flex items-center z-50 text-sm animate-slide-in">
          <CheckCircle className="h-4 w-4 mr-2" />
          {showSuccessMessage}
        </div>
      )}
      {showAddPlayer && isAdmin && (
        <AddPlayerModal
          onClose={() => setShowAddPlayer(false)}
          handleAddPlayer={handleAddPlayer}
        />
      )}
      {showDeleteConfirm && isAdmin && (
        <DeleteConfirmationModal
          onConfirm={() => handleMatchDelete(showDeleteConfirm, setShowDeleteConfirm)}
          onCancel={() => setShowDeleteConfirm(null)}
          message="이 경기를 삭제하시겠습니까? 이 작업은 취소할 수 없습니다."
        />
      )}
      <main id="main-content" tabIndex={-1} className="club-main">
        {authLoading && currentView !== 'requests' ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-secondary border-t-primary mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">로그인 정보 확인 중...</p>
            </div>
          </div>
        ) : (
          <Suspense fallback={<RouteLoadingFallback />}>
            {isAdminPage(currentView) && isAdmin ? <AdminWorkspace view={currentView} navigate={navigate}>{renderView()}</AdminWorkspace> : renderView()}
          </Suspense>
        )}
      </main>
      <OfflineIndicator />
      <SpeedInsights />
      <Analytics />
    </div>
  );
}

// AuthProvider로 전체 앱을 감싸기
export default function App() {
  return (
    <ErrorBoundary>
      <AppUpdateNotice />
      <AccessGate>
        <AuthProvider>
          <AdminProvider>
            <TennisRatingSystemContent />
          </AdminProvider>
        </AuthProvider>
      </AccessGate>
    </ErrorBoundary>
  );
}